import { Button, Col, Form, Input, message, Modal } from "antd";
import { useEffect, useState } from "react";
import { PatternFormat } from "react-number-format";
import { useSelector } from "react-redux";
import User from "redux/models/user";
import { selectUser, selectUserId } from "redux/slices/userSlice";
import K from "utilities/constants";
import {
  noTrailingSpaceAllowedRule,
  validateMobileNumber,
} from "utilities/generalUtility";
import { useParams } from "react-router-dom";
import AgGridUtility from "../../../../utilities/agGridUtility";
import { DataTableLayout } from "../../../../common/components/dataTableLayout/dataTableLayout";
import { ClientSideDataGrid } from "../../../../common/components/clientSideDataGrid/clientSideDataGrid";
import { usePageFilter } from "../../../../hooks/usePageFilters";
import { getColumns } from "./columns";

export default function ClientHiringManagers() {
  const { id } = useParams();

  const userId = useSelector(selectUserId);
  const userSlice = useSelector(selectUser);

  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [editData, setEditData] = useState({});
  const [hiringManagers, setHiringManagers] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const [search, setSearch] = usePageFilter(
    `clients_${id}_hiring_manager_search`,
  );

  const handleActivate = async (userId, isActive) => {
    try {
      await User.handleUserActive({ userId, isActive });

      getAllHiringManager();
    } catch (err) {
      console.error(err);
    }
  };

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleOk = () => {
    form.submit();
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const editRecord = (record) => {
    setEditData(record);
    showModal();
  };

  const afterModalClose = () => {
    setEditData({});
  };

  const onFinish = async (values) => {
    values = {
      ...values,
      isHiringManager: true,
      clientId: [id],
      userDesignationId: [6],
      roleId: 4,
      type: K.UserType.External.value,
      isManager: false,
    };
    setIsLoading(true);
    if (Object.keys(editData).length) {
      try {
        values = { ...values, user_id: editData.id };
        await User.updateUser(values, editData.id);
        getAllHiringManager();
        setIsLoading(false);
        setIsModalVisible(false);
        form.resetFields();
      } catch (err) {
        setIsLoading(false);
        console.error(err);
      }
      setEditData({});
    } else {
      try {
        await User.createUser(values);
        getAllHiringManager();
        setIsLoading(false);
        setIsModalVisible(false);
        form.resetFields();
      } catch (err) {
        setIsLoading(false);
        console.error(err);
      }
    }
  };

  const getAllHiringManager = async () => {
    try {
      const body = { clientIds: [id] };
      const res = await User.getClientHiringManager(body);

      setHiringManagers(res);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    form.resetFields();
  }, [editData]);

  useEffect(() => {
    getAllHiringManager();
  }, []);

  return (
    <>
      <DataTableLayout
        searchDefaultValue={search}
        onSearchChange={setSearch}
        actions={[
          {
            permissions: [K.Permissions.CreateJobHiringManagers],
            renderer: () => (
              <Col>
                <Button type="primary" onClick={showModal}>
                  Create
                </Button>
              </Col>
            ),
          },
        ]}
      >
        <ClientSideDataGrid
          userId={userId}
          configKey={K.AgGridTable.Keys.HiringManagerVisibleColumn}
          defaultColumnConfig={getColumns(
            userSlice.roles,
            editRecord,
            handleActivate,
          )}
          rowData={hiringManagers}
          quickFilterText={search}
          getContextMenuItems={() => AgGridUtility.menuItemsWithExport}
        />
      </DataTableLayout>

      <Modal
        forceRender
        className="s2-theme-style"
        title={`${
          Object.keys(editData).length ? "Edit" : "Create"
        } Hiring Manager`}
        open={isModalVisible}
        centered
        closeIcon={<i className="icon-closeable" />}
        okText={`${Object.keys(editData).length ? "Update" : "Create"}`}
        wrapClassName="vertical-center-modal"
        onOk={handleOk}
        okButtonProps={{ loading: isLoading }}
        onCancel={handleCancel}
        afterClose={afterModalClose}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={editData}
        >
          <Form.Item
            label="Name"
            name="name"
            rules={[
              { required: true, message: "Name is required." },
              noTrailingSpaceAllowedRule(),
            ]}
          >
            <Input placeholder="Enter name" />
          </Form.Item>
          <Form.Item
            label="Email Address"
            name="email"
            rules={[
              {
                required: true,
                message: "Email is required.",
              },
              {
                type: "email",
                message: "Enter a valid email address",
              },
            ]}
          >
            <Input placeholder="Enter email" />
          </Form.Item>
          <Form.Item
            label="Phone Number"
            name="phone"
            rules={[
              {
                required: true,
                message: "Phone Number is required.",
              },
              {
                validator: (_, value) => {
                  if (!value || validateMobileNumber(value)) {
                    return Promise.resolve();
                  }
                  return Promise.reject("Please enter correct number");
                },
              },
            ]}
          >
            <PatternFormat
              placeholder="Enter Phone Number"
              customInput={Input}
              format={K.PhoneFormat.USNational}
            />
          </Form.Item>
          <Form.Item
            label="Phone Ext"
            name="phoneExt"
            rules={[
              {
                pattern: new RegExp("^[0-9]{1,6}$", "g"),
                message: "Maximum of 6 digits is allowed",
              },
            ]}
          >
            <Input placeholder="Enter Phone Ext" />
          </Form.Item>
          <Form.Item label="Job Title" name="jobTitle">
            <Input placeholder="Enter job title" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
